import * as Icons from '@assets/icons/index';

import { Data } from './types';

const data: Data = {
  introduction: {
    title: 'Introduction',
    content: [
      {
        component: 'banner',
        id: 1,
        htmlContent:
          '<h1>Generative AI</h1><h2>Are you ready to seize the opportunity?</h2>',
        button: {
          title: 'Start the assessment',
        },
        video: true,
      },
      {
        component: 'typography',
        id: 2,
        htmlContent:
          '<p>GenerativeAI has the potential to deliver transformational benefits to the telecoms operator business. But how ready is your organization to deploy GenAI use cases at scale?</p><p>The <strong>Generative AI Maturity Interactive Tool</strong> is designed to provide your organization with an evaluation of your readiness to exploit the power of GenAI. You can compare your maturity with regional and global peers and with market leaders. And to help you on your journey we offer you insights and advice from TM Forum and AWS.</p>',
        button: {
          title: 'Start the assessment',
        },
      },
      {
        component: 'ctaForm',
        id: 3,
        htmlContent: '<h2>Already taken part? Retrieve your results</h2>',
        supplementaryHtmlContent:
          '<p>Please come back to review your saved assessment regularly – we’ll update it as our data grows.</p>',
      },
      {
        component: 'twoColumn',
        id: 4,
        htmlContent:
          '<h2>Objective</h2><h3>Defining opportunities</h3><p>The assessment sets out to identify where your organization needs to invest time and resources to better exploit the potential of AI. By comparing your maturity with other CSPs in six different categories (pillars) you can see where your organization is ready to accelerate its GenAI journey and where it needs to improve.</p>',
        backgroundColour: 'blue',
        graphs: [
          {
            percentage: 59,
            text: 'of CSPs do NOT have a CXO who is dedicated to AI',
          },
          {
            percentage: 20,
            text: 'of CSPs rely on systems integrators for delivering the skills that we need to leverage GenAI',
          },
        ],
      },
      {
        component: 'twoColumn',
        id: 5,
        htmlContent:
          '<h2>Recommendation</h2><h3>Accelerate your GenAI journey</h3><p>The Generative AI Maturity Interactive Tool is designed to provide your organization with an evaluation of your readiness to exploit the power of GenAI. You can compare your maturity with regional and global peers and with market leaders. And to help you on your journey we offer you insights and advice from TM Forum and AWS.</p><p>Please watch our <a href="https://iframe.dacast.com/vod/9292f2e21c51139fb9b2ffd6080ab1d6/e786e255-2187-45b0-b051-bb6c73f26ea3" target="_blank">introduction video on GAMIT</a> to learn more about the tool and watch the <a href="https://iframe.dacast.com/vod/9292f2e21c51139fb9b2ffd6080ab1d6/c3117d23-37ab-4cb7-ae36-3a2354ec7f55" target="_blank">tutorial</a> we created to help you use it to get the maximum out of it.</p>',
        image: require('../assets/temp/recommendation-video-image.png'),
        backgroundColour: 'white',
        button: {
          title: 'Start the assessment',
        },
      },
      {
        component: 'list',
        id: 6,
        htmlContent:
          "<h2>The 6 Pillars</h2><h3>Get ahead of the curve</h3><p>Gain valuable insights into your organization's AI capabilities and maturity level, enabling you to benchmark against industry standards and best practices and receive tailored recommendations that will empower you to prioritize investments, optimize resources, and effectively plan for the future of AI within your organization.</p>",
        pillarList: [
          {
            icon: Icons.iconPillarItem1,
            title: 'Technology understanding and maturity',
            text: 'Matching the right foundational model to the right use case and using an array of different techniques and approaches to generate the best results.',
            pillarIndex: 1,
          },
          {
            icon: Icons.iconPillarItem2,
            title: 'Organization, responsibilities, and skills',
            text: 'Ensuring that the right roles exist at the top of the organization, within the different areas of business and within technology functions to prioritize and execute GenAI strategies.',
            pillarIndex: 2,
          },
          {
            icon: Icons.iconPillarItem3,
            title: 'Data readiness and availability',
            text: 'Ensuring fast and easy access to high-quality data from the telecoms domain to monetize data assets and enable fine-tuning of models.',
            pillarIndex: 3,
          },
          {
            icon: Icons.iconPillarItem4,
            title: 'Governance, privacy, compliance, and security',
            text: 'Putting in place the requisite guardrails and policies to minimise the risk to the business of adverse consequences resulting from GenAI use case deployment.',
            pillarIndex: 4,
          },
          {
            icon: Icons.iconPillarItem5,
            title: 'Business objectives',
            text: 'Deciding on the right balance between using GenAI for value creation rather then efficiency gains.',
            pillarIndex: 5,
          },
          {
            icon: Icons.iconPillarItem6,
            title: 'Taking AI use cases into production',
            text: 'Making the evolution from proofs of concept (POCs) and minimum viable products (MVPs) to putting real use cases into production.',
            pillarIndex: 6,
          },
        ],
        button: {
          title: 'Start the assessment',
        },
      },
    ],
    href: '#introduction',
  },
  the_assessment: {
    title: 'The Assessment',
    content: [
      {
        component: 'survey',
        id: 1,
        pillars: [
          {
            id: 1,
            htmlContent: '<h2>Demographics</h2>',
            resultsHtmlContent: '',
            questions: [
              {
                question: 'In which region are you based?',
                answers: [
                  {
                    type: 'button',
                    text: 'North, Central and South America',
                  },
                  {
                    type: 'button',
                    text: 'Europe',
                  },
                  {
                    type: 'button',
                    text: 'Middle East & Africa',
                  },
                  {
                    type: 'button',
                    text: 'Asia / Asia-Pacific',
                  },
                ],
              },
              {
                question: 'How many customers are served by your organization?',
                answers: [
                  {
                    type: 'dragSingle',
                    text: '',
                    options: [
                      {
                        value: '< 5 million',
                      },
                      {
                        value: '5 to 25 million',
                      },
                      {
                        value: '25 to 50 million',
                      },
                      {
                        value: '50 to 100 million',
                      },
                      {
                        value: '> 100 million',
                      },
                    ],
                  },
                ],
              },
              {
                htmlContent: '',
                question: 'What are your responsibilities with respect to AI?',
                answers: [
                  {
                    type: 'button',
                    text: 'I have overall responsibility for my organization’s AI strategy and implementation and spend most of my time on AI',
                  },
                  {
                    type: 'button',
                    text: 'I have overall responsibility for my organization’s AI strategy and implementation but it represents a relatively small part of my role',
                  },
                  {
                    type: 'button',
                    text: 'I work within our central data / AI team and have some management responsibilities in AI',
                  },
                  {
                    type: 'button',
                    text: 'I am an AI practitioner within our central data / AI team',
                  },
                  {
                    type: 'button',
                    text: 'I work in the IT function and spend some of my time on AI',
                  },
                  {
                    type: 'button',
                    text: 'I work in the network function and spend some of my time on AI',
                  },
                  {
                    type: 'button',
                    text: 'I work in the B2B / B2C line of business with some responsibilities relating to AI',
                  },
                  {
                    type: 'button',
                    text: 'None of the above',
                  },
                ],
              },
            ],
          },

          {
            id: 2,
            htmlContent:
              '<h2>Pillar 01</h2><h3>Technology understanding and maturity</h3><p>Foundation models are the engine of generative AI (GenAI), and CSPs that aspire to achieve the best possible results – in terms of cost and return on investment (ROI) – from their GenAI use cases must learn how to best use the models available to them. Off-the-shelf GenAI models frequently fall short in optimizing complex, high-stakes workflows critical to telecoms operations.</p> <p>To overcome these limitations, CSPs must identify the specific areas where off-the-shelf models struggle to address the intricacies of telecoms-operator workflows. They can achieve this by continually adapting and specializing GenAI solutions through an iterative process, fine-tuning robust general models with their domain-specific data to ingest foundational industry knowledge. Performance monitoring helps CSPs identify quality gaps specific to each of their workflows.</p> <p>They can then address these gaps by developing dedicated AI components that tackle distinct sub-tasks, augmenting the overarching workflow capabilities. In summary, achieving the required quality for high-value workflows necessitates model specialization and continuous adaptation cycles.</p>',
            resultsHtmlContent:
              '<p>While most CSPs say that they have the skills to select the right LLM for the right use case the majority recognise that they are still in the relatively early stages of building the skills needed to optimise their accuracy. Similarly, most CSPs still have a long way to go when it comes to building the MLOps capabilities required to deploy GenAI use cases rapidly and profitably.</p>',
            questions: [
              {
                question:
                  'Do you believe that your organization has the right skills to select the right model for the right use case?',
                maxScore: 5,
                answers: [
                  {
                    type: 'button',
                    text: 'Yes',
                  },
                  {
                    type: 'button',
                    text: 'No',
                  },
                ],
              },
              {
                question:
                  'How would you assess your ability to use different techniques (RAG, prompt engineering, fine tuning etc) for getting the most accurate results for different use cases?',
                maxScore: 5,
                answers: [
                  {
                    type: 'button',
                    text: 'We are at the early stages of building our capabilities',
                  },
                  {
                    type: 'button',
                    text: 'We have some general capabilities but are not yet able to master these techniques for different use cases',
                  },
                  {
                    type: 'button',
                    text: 'We have built significant capabilities for using different techniques for different use cases',
                  },
                ],
              },
              {
                question:
                  'How would you assess your capabilities for assessing the cost and ROI of using different techniques (RAG, prompt engineering, fine tuning etc) for different use cases?',
                maxScore: 5,
                answers: [
                  {
                    type: 'button',
                    text: 'We are at the early stages of building our capabilities',
                  },
                  {
                    type: 'button',
                    text: 'We have some general capabilities but are not yet able to make decent assessments',
                  },
                  {
                    type: 'button',
                    text: 'We have built significant capabilities for assessing cost and ROI',
                  },
                ],
              },
              {
                question:
                  'Which of the following levels of MLOps maturity most closely aligns with your organization?',
                maxScore: 5,
                answers: [
                  {
                    type: 'button',
                    text: 'There are no formal or consistent processes for developing, deploying, or monitoring ML models. Projects are executed in isolation, with no  collaboration or integration with other teams or systems',
                  },
                  {
                    type: 'button',
                    text: 'Some fundamental MLOps practices and capabilities have been introduced. There is some collaboration and integration with other teams and systems and automation tools such as scripts and pipelines have started to be used',
                  },
                  {
                    type: 'button',
                    text: 'MLOps practices and capabilities such as CI/CD and continuous monitoring have been adopted. Robust and scalable automation tools such as orchestration platforms and cloud services are used for deploying ML models',
                  },
                  {
                    type: 'button',
                    text: 'MLOps practices have been fully integrated and optimized across the organization. Projects are aligned with business goals and outcomes, with clear and measurable value and impact. State-of-the-art automation tools such as serverless or edge computing are used for deploying ML models',
                  },
                ],
              },
            ],
          },
          {
            id: 3,
            htmlContent:
              '<h2>Pillar 02</h2><h3>Organization, responsibilities and skills</h3><p>Operators can accelerate GenAI adoption across their businesses by creating an optimal organizational structure. This should include accountabilities at the highest level, and putting in place the requisite skills and capabilities both at the center of the organization and in the divisions and departments which will adopt GenAI use cases.</p> <p>A Chief AI Officer or Chief Digital Officer provides the necessary leadership and authority. Supporting this executive function through a Center of Excellence (CoE), with well-defined roles on ethics, roadmap, tools, training and delivery, helps scale GenAI work across the organization. This drives consistency and avoids silos by providing consistent frameworks for guidelines, policies and processes for AI development lifecycle, data governance, model risk management, and ethical AI principles.</p><p>The CoE is best set up as a multidisciplinary team, having expertise across data science, engineering, domain knowledge, business, legal/compliance and ethics. It connects organizational units and enables knowledge sharing across the business. This will help CSPs drive AI literacy through training programs and build AI skills across the workforce. The adoption of KPIs and demonstrating tangible ROI can also help to build confidence and drive adoption across the organization.</p>',
            resultsHtmlContent:
              '<p>While the majority of CSPs have put in place an AI center of excellence they are not creating the required roles and capabilities in those divisions and functions across the business that will be the consumers of GenAI. When it comes to skills, some CSPs are investing in building GenAI capabilities to create a foundational expertise while others are taking a more pragmatic approach and continuing to rely heavily on systems integrators for deploying use cases.</p>',
            questions: [
              {
                question:
                  'When it comes to hiring and reskilling in specific areas relating to GenAI (eg prompt engineering) which of the following statements most closely aligns with your organization?',
                maxScore: 5,
                answers: [
                  {
                    type: 'button',
                    text: 'We are still assessing the skills that we need',
                  },
                  {
                    type: 'button',
                    text: 'We are in the process of recruiting skills to our central data /AI team',
                  },
                  {
                    type: 'button',
                    text: 'We have skilled up multiple teams and recruited a large number of skills in different AI categories',
                  },
                ],
              },
              {
                question:
                  'Do you have a CXO (for example a Chief Digital Officer or a Chief AI Officer) role whose time and responsibilities are predominantly (more than 50%) focussed on AI?',
                maxScore: 5,
                answers: [
                  {
                    type: 'button',
                    text: 'Yes',
                  },
                  {
                    type: 'button',
                    text: 'No',
                  },
                ],
              },
              {
                question:
                  'Do you have a central AI office / center of excellence that is responsible for AI governance?',
                maxScore: 5,
                answers: [
                  {
                    type: 'button',
                    text: 'Yes',
                  },
                  {
                    type: 'button',
                    text: 'No',
                  },
                ],
              },
              {
                question:
                  'Do you have dedicated AI roles and accountabilities in different parts of the business (eg product, network, marketing)?',
                maxScore: 5,
                answers: [
                  {
                    type: 'button',
                    text: 'Yes',
                  },
                  {
                    type: 'button',
                    text: 'No',
                  },
                ],
              },
            ],
          },
          {
            id: 4,
            htmlContent:
              '<h2>Pillar 03</h2><h3>Data readiness and availability</h3><p>Getting data AI-ready to deploy at scale, bringing it all together and consolidating it into a common data model, is critical. High-quality, domain-specific data and human oversight is crucial to allow CSPs to fine-tune generative foundation models trained on large data sets.</p> <p>This is a quicker, more cost-effective process than traditional model training. More generally, building trustworthy AI systems demands robust data management, model governance and responsible practices. CSPs can achieve this by cultivating data-centric cultures, curating high-quality data sets, and democratizing access to data across teams to unlock the full potential of GenAI at scale.</p>',
            resultsHtmlContent:
              '<p>Most AI-decision makes say they are reasonably happy with the quality of their and the access that they get to it. However, they are only at the early stages of developing strategies for using the unstructured data for which GenAI can deliver significant benefits.</p>',
            questions: [
              {
                question:
                  'How does your data perform in terms of the following dimensions and capabilities?',
                maxScore: 5,
                answers: [
                  {
                    type: 'drag',
                    text: 'Rapid access to the data',
                    options: [
                      {
                        value: '1',
                      },
                      {
                        value: '2',
                      },
                      {
                        value: '3',
                      },
                      {
                        value: '4',
                      },
                      {
                        value: '5',
                      },
                    ],
                  },
                  {
                    type: 'drag',
                    text: 'Access to trustworthy data',
                    options: [
                      {
                        value: '1',
                      },
                      {
                        value: '2',
                      },
                      {
                        value: '3',
                      },
                      {
                        value: '4',
                      },
                      {
                        value: '5',
                      },
                    ],
                  },
                  {
                    type: 'drag',
                    text: 'Access to high-quality structured data',
                    options: [
                      {
                        value: '1',
                      },
                      {
                        value: '2',
                      },
                      {
                        value: '3',
                      },
                      {
                        value: '4',
                      },
                      {
                        value: '5',
                      },
                    ],
                  },
                  {
                    type: 'drag',
                    text: 'Access to high-quality unstructured data',
                    options: [
                      {
                        value: '1',
                      },
                      {
                        value: '2',
                      },
                      {
                        value: '3',
                      },
                      {
                        value: '4',
                      },
                      {
                        value: '5',
                      },
                    ],
                  },
                  {
                    type: 'drag',
                    text: 'Access to streaming data',
                    options: [
                      {
                        value: '1',
                      },
                      {
                        value: '2',
                      },
                      {
                        value: '3',
                      },
                      {
                        value: '4',
                      },
                      {
                        value: '5',
                      },
                    ],
                  },
                ],
              },
            ],
          },
          {
            id: 5,
            htmlContent:
              '<h2>Pillar 04</h2><h3>Governance, privacy, compliance and security</h3><p>CSPs should establish a comprehensive governance framework to steer their GenAI initiatives. This can be enabled by a centralized platform that handles security, compliance and cost optimization aspects through robust controls and monitoring. Synergies can be realized through a cross-functional Center of Excellence (CoE), with specialist experts across functions and business domains put in place to enable rapid decision-making. Sitting above these roles and functions, an executive governing body or steering committee is needed to oversee and make ultimate decisions on strategy and policies.</p><p>This set-up supports the creation of well-defined processes and policies for reviewing proposed GenAI models and use cases through a risk/opportunity lens. And it helps with evaluating privacy, security, ethics and regulatory compliance before deployment, as well as enabling fast, agile change management as required. Strict governance programs can also help CSPs to develop technologies and capabilities that they can monetize externally.</p>',
            resultsHtmlContent:
              '<p>While most CSPs have put AI governance programs in place, many are still working through issues specific to GenAI and relating to security, privacy and IP.</p>',
            questions: [
              {
                question: 'Does your GenAI governance include the following?',
                maxScore: 5,
                answers: [
                  {
                    type: 'multiSelect',
                    text: 'A GenAI platform that handles aspects relating to security / compliance / cost issues',
                    options: [
                      {
                        value: 'yes',
                      },
                      {
                        value: 'no',
                      },
                    ],
                  },
                  {
                    type: 'multiSelect',
                    text: 'A cross-functional GenAI center of excellence (COE) to supplement a central function',
                    options: [
                      {
                        value: 'yes',
                      },
                      {
                        value: 'no',
                      },
                    ],
                  },
                  {
                    type: 'multiSelect',
                    text: 'A core governing body, including an ultimate decision-maker for the organization’s GenAI agenda',
                    options: [
                      {
                        value: 'yes',
                      },
                      {
                        value: 'no',
                      },
                    ],
                  },
                  {
                    type: 'multiSelect',
                    text: 'A well-defined process / policy that reviews the model / use cases and addresses potential negative impact',
                    options: [
                      {
                        value: 'yes',
                      },
                      {
                        value: 'no',
                      },
                    ],
                  },
                ],
              },
            ],
          },
          {
            id: 6,
            htmlContent:
              "<h2>Pillar 05</h2><h3>Business objectives</h3><p><p>Generative AI has the potential to drive transformative change for operators by enabling near-human interactions and accelerating broader AI adoption across the organization. To truly harness its impact, CSPs must make the transition from undertaking disconnected AI initiatives to being truly AI-native.</p><p>This involves using GenAI to deliver improvements to high-value workflows as business needs evolve. By leveraging GenAI's capabilities across critical business functions within the organization, CSPs can deliver better outcomes and deliver company-wide transformations that go beyond the traditional approach of delivering incremental change.</p>",
            resultsHtmlContent:
              '<p>Chatbot and summarization are the use cases which have been most widely put into production by CSPs although a large number are evaluating and building POCs for code, document and content creation. The focus today is more on efficiency gains than value creation although this does vary based on the specific use case.</p>',
            questions: [
              {
                question:
                  'Which of the following domains / use cases are you building GenAI applications for today?',
                maxScore: 2,
                answerLabels: [
                  {
                    value: 'Evaluating',
                  },
                  {
                    value: 'Building POCs / MVPs',
                  },
                  {
                    value: 'Putting into production',
                  },
                ],
                answers: [
                  {
                    type: 'radial',
                    text: 'Chatbots',
                    options: [
                      {
                        value: 'Evaluating',
                      },
                      {
                        value: 'Building POCs / MVPs',
                      },
                      {
                        value: 'Putting into production',
                      },
                    ],
                  },
                  {
                    type: 'radial',
                    text: 'Summarization',
                    options: [
                      {
                        value: 'Evaluating',
                      },
                      {
                        value: 'Building POCs / MVPs',
                      },
                      {
                        value: 'Putting into production',
                      },
                    ],
                  },
                  {
                    type: 'radial',
                    text: 'Document creation',
                    options: [
                      {
                        value: 'Evaluating',
                      },
                      {
                        value: 'Building POCs / MVPs',
                      },
                      {
                        value: 'Putting into production',
                      },
                    ],
                  },
                  {
                    type: 'radial',
                    text: 'Code creation',
                    options: [
                      {
                        value: 'Evaluating',
                      },
                      {
                        value: 'Building POCs / MVPs',
                      },
                      {
                        value: 'Putting into production',
                      },
                    ],
                  },
                  {
                    type: 'radial',
                    text: 'Content creation',
                    options: [
                      {
                        value: 'Evaluating',
                      },
                      {
                        value: 'Building POCs / MVPs',
                      },
                      {
                        value: 'Putting into production',
                      },
                    ],
                  },
                ],
              },
              {
                question:
                  'What are your business objectives today for the application of GenAI in different departments within your organization?',
                maxScore: 3,
                answerLabels: [
                  {
                    value: 'Principally efficiency gains',
                  },
                  {
                    value: 'Principally value creation',
                  },
                  {
                    value:
                      'Evenly divided between efficiency and value creation',
                  },
                ],
                answers: [
                  {
                    type: 'radial',
                    text: 'IT function',
                    options: [
                      {
                        value: 'Principally efficiency gains',
                      },
                      {
                        value: 'Principally value creation',
                      },
                      {
                        value:
                          'Evenly divided between efficiency and value creation',
                      },
                    ],
                  },
                  {
                    type: 'radial',
                    text: 'Network operations',
                    options: [
                      {
                        value: 'Principally efficiency gains',
                      },
                      {
                        value: 'Principally value creation',
                      },
                      {
                        value:
                          'Evenly divided between efficiency and value creation',
                      },
                    ],
                  },
                  {
                    type: 'radial',
                    text: 'Sales and marketing',
                    options: [
                      {
                        value: 'Principally efficiency gains',
                      },
                      {
                        value: 'Principally value creation',
                      },
                      {
                        value:
                          'Evenly divided between efficiency and value creation',
                      },
                    ],
                  },
                  {
                    type: 'radial',
                    text: 'Product organizations (for the creation of new / enhanced products)',
                    options: [
                      {
                        value: 'Principally efficiency gains',
                      },
                      {
                        value: 'Principally value creation',
                      },
                      {
                        value:
                          'Evenly divided between efficiency and value creation',
                      },
                    ],
                  },
                ],
              },
            ],
          },
          {
            id: 7,
            htmlContent:
              '<h2>Pillar 06</h2><h3>Taking AI use cases into production</h3><p>Most CSPs with GenAI aspirations have built several proofs of concept (POCs). But putting use cases into production has sometimes proved challenging. There are many reasons why a project may not make it into production. It may be that it did not generate the required business value. A failure to deliver accurate results is another common issue.</p><p>One approach to simplify and accelerate the process of moving from POC or minimum viable product to production involves creating reusable building blocks which can then be rapidly assembled and reassembled based on evolving business needs. Through a composable, cloud-native architecture, these building blocks can be blended with specialized common functions as shared services. This approach also enables rapid experimentation, iteration and refinement of GenAI models.</p>',
            resultsHtmlContent:
              '<p>The majority of CSPs have less than five GenAI use cases in production and less than one in three has built a strong pipeline. Few of those operators that have put use cases into production are fine tuning LLMs and a majority are trying out different foundational models before choosing the right one for a specific use case.</p>',
            questions: [
              {
                question:
                  'Where are you today in terms of putting GenAI use cases into production?',
                maxScore: 7,
                answers: [
                  {
                    type: 'button',
                    text: 'We are observing what other CSPs are doing with a view to setting up experiments / POCs',
                  },
                  {
                    type: 'button',
                    text: 'We have created some POCs but have not converted these into full deployments',
                  },
                  {
                    type: 'button',
                    text: 'We have deployed a handful of use cases but have not yet built an AI pipeline',
                  },
                  {
                    type: 'button',
                    text: 'We have deployed a number of use cases and have a strong pipeline',
                  },
                ],
              },
              {
                question: 'How many GenAI use cases do you have in production?',
                maxScore: 7,
                answers: [
                  {
                    type: 'dragSingle',
                    text: '',
                    options: [
                      {
                        value: '0',
                      },
                      {
                        value: '1-2',
                      },
                      {
                        value: '3-5',
                      },
                      {
                        value: '6-10',
                      },
                      {
                        value: '>10',
                      },
                    ],
                  },
                ],
              },
              {
                question:
                  'For the POCs that have moved into production, which of the following statements is closest to how you have approached your choice of LLM and how you have gone about achieving the best results?',
                maxScore: 7,
                answers: [
                  {
                    type: 'button',
                    text: 'We have not moved any POCs into production',
                  },
                  {
                    type: 'button',
                    text: 'We tested multiple LLMs before selecting the one that delivered the right result',
                  },
                  {
                    type: 'button',
                    text: 'We assessed the merits of using techniques such as RAG and prompt engineering and used them where necessary',
                  },
                  {
                    type: 'button',
                    text: 'We tested multiple LLMs and, where necessary, fine-tuned the model. In addition, we assessed the merits of using techniques such as RAG and prompt engineering and used them where necessary',
                  },
                ],
              },
            ],
          },
        ],
      },
    ],
    href: '#the-assessment',
  },
  results: {
    title: 'Results',
    content: [
      {
        component: 'resultsBanner',
        id: 1,
        htmlContent:
          '<h1>Your Generative AI maturity revealed</h1><p>CSPs are still in the relatively early stages of deploying GenAI use cases. Your maturity score will reveal the extent to which you have put the right foundations in place to use GenAI at scale and deploy use cases positively and with a strong ROI.</p>',
        button: {
          href: '#',
          title: 'Download your results',
          className: 'pdf-hide',
        },
      },
      {
        component: 'resultsYourResults',
        id: 2,
        htmlContent:
          '<h2 class="pillar-title">Your Results</h2><h3>How you measure up</h3><p>By comparing your own maturity score for each of the six pillars with the entire CSP community, with CSPs in your region and with market leaders, you can assess the parts of the business that you need to invest in for you to accelerate your GenAI journey.</p>',
      },
      {
        component: 'cta',
        id: 3,
        htmlContent:
          '<h3>Download your results</h3><p>Download an in-depth PDF of your results.</p>',
        button: {
          href: '#',
          title: 'Download your results',
          className: 'pdf-hide',
        },
      },
      {
        component: 'resultsPillar',
        id: 4,
        awsButton: {
          href: 'https://aws.amazon.com/telecom/contact-us/?trk=4369aadf-04bc-4b7d-9c85-dd1a55e09c7c&sc_channel=el',
          title: 'Contact AWS',
        },
        awsHtmlContent:
          '<h3>AWS offers you a list of curated assets on the topic of Technology understanding and maturity that we encourage you to check out:</h3><ol><li>Watch Mark Newman from TM Forum and Ross McWalter from AWS tackle <a href="https://iframe.dacast.com/vod/9292f2e21c51139fb9b2ffd6080ab1d6/54db53cf-4882-4be5-93e8-acf2f9c52015" target="_blank">Technology understanding and maturity in their GAMIT video series.</a></li><li>Explore our <a href="https://pages.awscloud.com/GLOBAL-acq-IND-generative-AI-Telco-2024-reg.html" target="_blank">AWS generative AI demonstration repository</a> and see how your peers across the industry have addressed specific topics and use cases. We especially recommend watching SKT with Anthropic on model tuning as part of the generative AI learning series, and Ericsson on telecom specific LLMs with ELI.</li><li>Check out the webinar with GSMA, Deutsche Telekom, and AWS on Mobile World Live that looks at the future of generative AI for telcos and at <a href="https://www.mobileworldlive.com/webinar-generative-ai-is-there-a-future-for-the-generative-telco/" target="_blank">best practice lessons from the Deutsche Telekom journey</a>.</li><li>Learn about <a href="https://catalog.us-east-1.prod.workshops.aws/workshops/bb62b5d7-313f-4733-88cd-9c1aa41c724d/en-US" target="_blank">Large Generative AI model hosting</a> in this self-paced workshop suited for upskilling your technical experts and decision makers; including lab exercises to deploy on cloud.</li><li>Contact us to evaluate the opportunity of a generative AI discovery workshop for your organization and how you can tap further into the training offers and capabilities that might already be available for you. And if you want to start building a generative AI education initiative or program for your people with a focus on technology maturity, there was never a better time than now. Select your choice from the contact form for your preference, and we will be in touch.</li><li>Read our <a href="https://aws.amazon.com/blogs/industries/opportunities-for-telecoms-with-small-language-models/" target="_blank">in-depth blog</a> about opportunities for telecoms with small language models with insights from AWS and Meta.</li></ol>',
        htmlContent: '<h3>Technology understanding and maturity</h3>',
        tmHtmlContent:
          '<p>Foundation models are the engine of generative AI (GenAI), and CSPs that aspire to achieve the best possible results – in terms of cost and return on investment (ROI) – from their GenAI use cases must learn how to best use the models available to them. Off-the-shelf GenAI models frequently fall short in optimizing complex, high-stakes workflows critical to telecoms operations.</p><p>To overcome these limitations, CSPs must identify the specific areas where off-the-shelf models struggle to address the intricacies of telecoms-operator workflows. They can achieve this by continually adapting and specializing GenAI solutions through an iterative process, fine-tuning robust general models with their domain-specific data to ingest foundational industry knowledge.</p> <p>Performance monitoring helps CSPs identify quality gaps specific to each of their workflows. They can then address these gaps by developing dedicated AI components that tackle distinct sub-tasks, augmenting the overarching workflow capabilities. In summary, achieving the required quality for high-value workflows necessitates model specialization and continuous adaptation cycles.</p>',
        tmButton: {
          href: 'https://www.tmforum.org/gamit-pillar-1-technology-understanding-and-maturity',
          title: 'Read more from TM Forum',
        },
        backgroundColour: 'white',
        pillarIndex: 1,
        charts: [
          {
            questionIndex: 0,
            type: 'radial',
          },
          {
            questionIndex: 1,
            type: 'radial',
          },
          {
            questionIndex: 2,
            type: 'radial',
          },
          {
            questionIndex: 3,
            type: 'radial',
          },
        ],
      },
      {
        component: 'resultsPillar',
        id: 5,
        htmlContent: '<h3>Organization, responsibilities and skills</h3>',
        awsButton: {
          href: 'https://aws.amazon.com/telecom/contact-us/?trk=c83cad30-8f16-472e-814a-332db248a226&sc_channel=el',
          title: 'Contact AWS',
        },
        awsHtmlContent:
          '<h3>AWS offers you a list of curated assets on the topic of Organization, responsibilities and skills that we encourage you to check out:</h3><ol><li>Watch Mark Newman from TM Forum and Ross McWalter from AWS tackle <a href="https://iframe.dacast.com/vod/9292f2e21c51139fb9b2ffd6080ab1d6/f1af1083-ad26-4edf-9976-f3f32284d741" target="_blank">organization, responsibility and skills in their GAMIT video series.</a></li><li>Explore our <a href="https://aws.amazon.com/telecom/future-of-telecom-with-generative-ai/" target="_blank">AWS generative AI demonstration repository</a> and see how your peers across the industry have addressed specific topics and use cases. We especially recommend watching scaling generative AI through collaboration as part of the generative AI learning series.</li><li>Learn about the <a href="https://catalog.us-east-1.prod.workshops.aws/workshops/a4bdb007-5600-4368-81c5-ff5b4154f518/en-US" target="_blank">Generative AI Skills Workshop</a></li><li>Dive deeper with the <a href="https://catalog.us-east-1.prod.workshops.aws/workshops/b6058098-b087-400c-840a-034742a95f08/en-US" target="_blank">AI/ML and generative AI workshop for telcos</a> to learn how you can apply AWS Machine Learning and Generative AI tools to analytics and operations use cases in a telco context.</li><li>Contact us to evaluate the opportunity of discussing how AWS can help you transform your organization to become AI-native and how to lay the foundations to attract, retain and develop the talent you need to become an AI-first thinking telco.</li></ol>',
        tmHtmlContent:
          '<p>Operators can accelerate GenAI adoption across their businesses by creating an optimal organizational structure. This should include accountabilities at the highest level, and putting in place the requisite skills and capabilities both at the center of the organization and in the divisions and departments which will adopt GenAI use cases. A Chief AI Officer or Chief Digital Officer provides the necessary leadership and authority. Supporting this executive function through a Center of Excellence (CoE), with well-defined roles on ethics, roadmap, tools, training and delivery, helps scale GenAI work across the organization.</p><p>This drives consistency and avoids silos by providing consistent frameworks for guidelines, policies and processes for AI development lifecycle, data governance, model risk management, and ethical AI principles. The CoE is best set up as a multidisciplinary team, having expertise across data science, engineering, domain knowledge, business, legal/compliance and ethics. It connects organizational units and enables knowledge sharing across the business. This will help CSPs drive AI literacy through training programs and build AI skills across the workforce. The adoption of KPIs and demonstrating tangible ROI can also help to build confidence and drive adoption across the organization.</p>',
        tmButton: {
          href: 'https://www.tmforum.org/gamit-pillar-2-organization-responsibilities-and-skills',
          title: 'Read more from TM Forum',
        },
        backgroundColour: 'blue',
        pillarIndex: 2,
        charts: [
          {
            questionIndex: 0,
            type: 'radial',
          },
          {
            questionIndex: 1,
            type: 'radial',
          },
          {
            questionIndex: 2,
            type: 'radial',
          },
          {
            questionIndex: 3,
            type: 'radial',
          },
        ],
      },
      {
        component: 'resultsPillar',
        id: 6,
        htmlContent: '<h3>Data readiness and availability</h3>',
        awsButton: {
          href: 'https://aws.amazon.com/telecom/contact-us/?trk=79217840-67d2-4b6f-9cfe-2f87f90a849a&sc_channel=el',
          title: 'Contact AWS',
        },
        awsHtmlContent:
          '<h3>AWS offers you a list of curated assets on the topic of data readiness and availability that we encourage you to check out:</h3><ol><li>Watch Mark Newman from TM Forum and Ross McWalter from AWS tackle <a href="https://iframe.dacast.com/vod/9292f2e21c51139fb9b2ffd6080ab1d6/805dbc63-5331-4cbb-8a2e-b46cf38f86eb" target="_blank">data readiness and availability in their GAMIT video series.</a></li><li>Explore the <a href="https://aws.amazon.com/telecom/future-of-telecom-with-generative-ai/" target="_blank">AWS generative AI demonstration repository</a> and see how your peers across the industry have addressed specific topics and use cases.</li><li>Hear from telecom operators, such as Telia, on how they use generative AI to enable high quality data as a source for their overall AI/ML strategy together with Anthropic and Accenture.(Link to interview with Henrik Hofling, tbd!)</li><li>Listen to Larry Ellison from Oracle and Matt Garman from AWS on how their partnership uniquely offers zero-ETL integrations to AWS Analytics services, which enables customers to use both their <a href="https://www.youtube.com/live/1Nn8ADoWbKk?t=337s" target="_blank">data on Oracle and on AWS to remove data silos</a>, enabling generative AI.</li><li>Read our expert blog on <a href="https://aws.amazon.com/blogs/big-data/data-governance-in-the-age-of-generative-ai/" targe="_blank">data governance in the age of generative AI</a> to make data your key differentiator and help develop comprehensive strategy for data management.</li><li>Investigate how to accelerate your generative AI journey using your data on AWS and look beyond your industry into <a href="https://aws.amazon.com/ai/generative-ai/data/?trk=fa1e6d3a-d717-47a1-bad7-d05751d56785&sc_channel=el" target="_blank">inspiring cases of data foundations for generative AI</a> from Gilead, Pinterest, the PGA, Intuit and Perplexity.</li><li>Check out our white paper on <a href="https://pages.awscloud.com/global-ln-gc-600-genai-adv-tdm-learn.html?trk=da8683bc-e6a5-47e5-96dd-52e1229ccaea&sc_channel=el" target="_blank">how to power generative AI with your data</a> and how to make AI work for you.</li><li>Contact us to evaluate the opportunity of a “data for generative AI” workshop for your organization, how to democratize your data and provide data governance. Select your choice from the contact form for your preference, and we will be in touch.</li></ol>',
        tmHtmlContent:
          '<p>Getting data AI-ready to deploy at scale, bringing it all together and consolidating it into a common data model, is critical. High-quality, domain-specific data and human oversight is crucial to allow CSPs to fine-tune generative foundation models trained on large data sets. This is a quicker, more cost-effective process than traditional model training. More generally, building trustworthy AI systems demands robust data management, model governance and responsible practices. CSPs can achieve this by cultivating data-centric cultures, curating high-quality data sets, and democratizing access to data across teams to unlock the full potential of GenAI at scale.</p>',
        tmButton: {
          href: 'https://www.tmforum.org/gamit-pillar-3-data-readiness-and-availability',
          title: 'Read more from TM Forum',
        },
        backgroundColour: 'white',
        pillarIndex: 3,
        charts: [
          {
            questionIndex: 0,
            type: 'bar',
          },
        ],
      },
      {
        component: 'resultsPillar',
        id: 7,
        htmlContent: '<h3>Governance, privacy, compliance and security</h3>',
        awsButton: {
          href: 'https://aws.amazon.com/telecom/contact-us/?trk=11f9a347-4ac7-4d11-b072-e80bbab8de6a&sc_channel=el',
          title: 'Contact AWS',
        },
        awsHtmlContent:
          '<h3>AWS offers you a list of curated assets on the topic of Governance, privacy, compliance and security that we encourage you to check out:</h3><ol><li>Watch Mark Newman from TM Forum and Ross McWalter from AWS reflect on <a href="https://iframe.dacast.com/vod/9292f2e21c51139fb9b2ffd6080ab1d6/dea198f7-34d1-4304-a62e-b11c2a84a66c" target="_blank">Governance, privacy, compliance and security in their GAMIT video series.</a></li><li>The <a href="https://aws.amazon.com/ai/generative-ai/security/scoping-matrix/" target="_blank">Generative AI Security Scoping Matrix</a> is a comprehensive framework designed to help organizations assess and implement security controls throughout the AI lifecycle.</li> <li>A <a href="https://aws.amazon.com/blogs/machine-learning/a-secure-approach-to-generative-ai-with-aws/" target="_blank">secure approach to generative AI with AWS</a> is described in this AWS Machine Learning Blog</li><li>Explore the <a href="https://docs.aws.amazon.com/wellarchitected/latest/security-pillar/welcome.html" target="_blank">Security Pillar of the AWS Well-Architected Framework</a> for guidance to help you apply best practices, current recommendations in the design, delivery, and maintenance of secure AWS workloads while using generative AI</li><li>Check our resources on <a href="https://aws.amazon.com/machine-learning/responsible-ai/" target="_blank">transforming responsible AI from theory into practice</a> by promoting the safe and responsible development of AI</li><li>Contact us to evaluate the opportunity of a workshop or engagement focused on securing your generative AI deployments to ensure privacy, compliance and proper governance is in place. Select your choice from the contact form for your preference, and we will be in touch.</li></ol>',
        tmHtmlContent:
          '<p>CSPs should establish a comprehensive governance framework to steer their GenAI initiatives. This can be enabled by a centralized platform that handles security, compliance and cost optimization aspects through robust controls and monitoring. Synergies can be realized through a cross-functional Center of Excellence (CoE), with specialist experts across functions and business domains put in place to enable rapid decision-making. Sitting above these roles and functions, an executive governing body or steering committee is needed to oversee and make ultimate decisions on strategy and policies.</p><p>This set-up supports the creation of well-defined processes and policies for reviewing proposed GenAI models and use cases through a risk/opportunity lens. And it helps with evaluating privacy, security, ethics and regulatory compliance before deployment, as well as enabling fast, agile change management as required. Strict governance programs can also help CSPs to develop technologies and capabilities that they can monetize externally.</p>',
        tmButton: {
          href: 'https://www.tmforum.org/gamit-pillar-4-governance-privacy-compliance-and-security',
          title: 'Read more from TM Forum',
        },
        backgroundColour: 'blue',
        pillarIndex: 4,
        charts: [
          {
            questionIndex: 0,
            type: 'bar',
          },
        ],
      },
      {
        component: 'resultsPillar',
        id: 8,
        htmlContent: '<h3>Business objectives</h3>',
        awsButton: {
          href: 'https://aws.amazon.com/telecom/contact-us/?trk=b09f4ec3-2c9a-4283-a51a-52d53dce7895&sc_channel=el',
          title: 'Contact AWS',
        },
        awsHtmlContent:
          '<h3>AWS offers you a list of curated assets on the topic of generative AI business objectives that we encourage you to check out:</h3><ol><li>Watch Mark Newman from TM Forum and Ross McWalter from AWS reflect on <a href="https://iframe.dacast.com/vod/9292f2e21c51139fb9b2ffd6080ab1d6/9c945ac3-718d-496e-8a1f-32525f1ad727" target="_blank">business objectives and use cases in their GAMIT video series.</a></li><li>Hear what the most common <a href="https://pages.awscloud.com/GLOBAL-acq-DL-AWS-Anthropic-2024-learn.html" target="_blank">telco use cases with foundation model provider Anthropic on AWS</a> are and see how SK Telecom, T-Mobile US, Telia Company, COX and Globe Telecom are using generative AI on AWS to unlock value.</li><li>Explore our <a href="https://aws.amazon.com/telecom/future-of-telecom-with-generative-ai/" target="_blank">AWS generative AI demonstration repository</a> and see how your peers across the industry have addressed specific topics and use cases. We especially recommend use case specific demos with Orange on networks, Salesforce on cost and revenue improvements, and Liberty Latin America on insights for contact centers.</li><li>Dive deep into <a href="https://www.youtube.com/watch?v=0n-nM3LNMko" target="_blank">Increasing Developer Productivity with Generative AI</a> on the example of BT.</li><li>See how generative AI applications in the area of IoT can deliver <a href="https://www.youtube.com/watch?v=y7R-onhEdNI&t=6s" target="_blank">new revenue opportunities for telcos in the smart home market</a> with the example of TELUS.</li><li>Contact us to evaluate the opportunity of discussing how AWS can help you identify the use cases that perfectly match your business objectives and how to leverage the knowledge and experience gained from implementing similar cases within the telco industry. Explore with us how rapid prototyping can take you from ideation to design to proof of value quickly to close gaps and add generative AI support to your high-value workflows.</li></ol>',
        tmHtmlContent:
          "<p>Generative AI has the potential to drive transformative change for operators by enabling near-human interactions and accelerating broader AI adoption across the organization. To truly harness its impact, CSPs must make the transition from undertaking disconnected AI initiatives to being truly AI-native. This involves using GenAI to deliver improvements to high-value workflows as business needs evolve. By leveraging GenAI's capabilities across critical business functions within the organization, CSPs can deliver better outcomes and deliver company-wide transformations that go beyond the traditional approach of delivering incremental change.</p>",
        tmButton: {
          href: 'https://www.tmforum.org/gamit-pillar-5-business-objectives',
          title: 'Read more from TM Forum',
        },
        backgroundColour: 'white',
        pillarIndex: 5,
        charts: [
          {
            questionIndex: 0,
            type: 'bar',
          },
          {
            questionIndex: 1,
            type: 'bar',
          },
        ],
      },
      {
        component: 'resultsPillar',
        id: 9,
        htmlContent: '<h3>Taking AI use cases into production</h3>',
        awsButton: {
          href: 'https://aws.amazon.com/telecom/contact-us/?trk=39ab9e39-5fb2-4dff-8f08-05595c17b312&sc_channel=el',
          title: 'Contact AWS',
        },
        awsHtmlContent:
          '<h3>AWS offers you a list of curated assets on the topic of taking AI use cases into production that we encourage you to check out:</h3><ol><li>Watch Mark Newman from TM Forum and Ross McWalter from AWS talk about <a href="https://iframe.dacast.com/vod/9292f2e21c51139fb9b2ffd6080ab1d6/1335527a-daa5-4d93-808b-f2d47469ec53" target="_blank">taking AI use cases into production in their GAMIT video series.</a></li><li><a href="https://inform.tmforum.org/dtw/aws-on-making-genai-ready-for-prime-time-sameer-vuyyuru" target="_blank">Making generative AI ready for prime time</a> - Sameer Vuyyuru from AWS takes a look on how to orchestrate multiple domain-specific and use case specific models at scale.</li><li>Explore our <a href="https://aws.amazon.com/telecom/future-of-telecom-with-generative-ai/" target="_blank">AWS generative AI demonstration repository</a> and see how your peers across the industry have addressed specific topics and use cases. We especially recommend AWS Chris Featherstone talking about scaling generative AI through collaboration.</li><li>Explore accelerating developer productivity with <a href="https://aws.amazon.com/blogs/aws/aws-named-as-a-leader-in-the-first-gartner-magic-quadrant-for-ai-code-assistants/" target="_blank">generative AI based coding assistants</a>.</li><li>Learn how BT takes <a href="https://inform.tmforum.org/dtw/how-to-successfully-accelerate-generative-ai-deployments-at-scale-and-improve-developer-productivity-tony-poulos-sameer-vuyyuru-and-deepika-adusumilli" target="_blank">generative AI to deployment at scale</a> through their Generative AI Gateway.</li><li>Contact us to evaluate the opportunity of discussing how AWS can help you accelerate use case deployments, e.g. through the generative AI launchpad. Explore with us how you can scale your use of generative AI successfully across high value workflows in multiple domains towards your business goals successfully.</li></ol>',
        tmHtmlContent:
          '<p>Most CSPs with GenAI aspirations have built several proofs of concept (POCs). But putting use cases into production has sometimes proved challenging. There are many reasons why a project may not make it into production. It may be that it did not generate the required business value. A failure to deliver accurate results is another common issue. One approach to simplify and accelerate the process of moving from POC or minimum viable product to production involves creating reusable building blocks which can then be rapidly assembled and reassembled based on evolving business needs. Through a composable, cloud-native architecture, these building blocks can be blended with specialized common functions as shared services. This approach also enables rapid experimentation, iteration and refinement of GenAI models.</p>',
        tmButton: {
          href: 'https://www.tmforum.org/gamit-pillar-6-taking-ai-use-cases-into-production',
          title: 'Read more from TM Forum',
        },
        backgroundColour: 'blue',
        pillarIndex: 6,
        charts: [
          {
            questionIndex: 0,
            type: 'radial',
          },
          {
            questionIndex: 1,
            type: 'radial',
          },
          {
            questionIndex: 2,
            type: 'radial',
          },
        ],
      },
    ],
    href: '#results',
  },
  our_methodology: {
    title: 'Our Methodology',
    content: [
      {
        component: 'banner',
        id: 1,
        htmlContent: '<h1>Methodology</h1>',
        video: false,
      },
      {
        component: 'typography',
        id: 2,
        htmlContent:
          '<p>For the maturity tool we have identified six categories (pillars) which, when considered together, provide a good indication of how ready a telecoms operator is to deploy GenAI use cases at scale. The six pillars are:</p><ul><li>Technology Understanding and Maturity</li><li>Organization, Responsibilities and Skills</li><li>Data Readiness and Availability</li><li>Governance, Privacy, Compliance and Security</li><li>Business Objectives</li><li>Taking AI Use Cases into Production</li></ul><p>For each pillar you are invited to answer a handful of questions about your organization. We then compare these answers with those provided by more than 200 AI-decision makers in CSPs globally. We then provide you with a score for each pillar and an overall assessment.</p> <p>Over the next weeks and months, we will add curated input of additional assessments into the tool to refine the industry benchmark. As a result, the quality and depth of the benchmark increases as you and your peers share input. Please revisit the tool at regular intervals to update your insights.</p>',
      },
    ],
    href: '#our-methodology',
  },
};

export default data;
